import contractService from "@services/contract/contractService"
import type {
  IExportContractEventConvertFromContractInput,
  IExportContractEventSalesPolicyFromContractInput,
  IExportGeneratedPaymentScheduleDocxInput,
  IExportGeneratedPaymentScheduleInput,
  IPrepareExportContractEventConvertResult,
  IPrepareExportContractEventSalesPolicyResult,
} from "@services/contract/contractService"
import type { IExportQueuedResult } from "@lib/helperFile"
import type { PagedResultDto } from "@services/dto/pagedResultDto"
import { ContractEventPromotionProgramModel, resolveContractPromotionPrograms } from "@models/contract/contractModel"
import { ContractEventTypeEnum } from "@lib/enum"
import { action, makeAutoObservable, observable } from "mobx"

class ContractStore {
  @observable isLoading!: boolean
  /** Cờ loading riêng cho luồng xuất file sự kiện chuyển đổi (Feature-0371) — xem chú thích ở action. */
  @observable isLoadingExportEventConvert = false
  /** Cờ loading riêng cho luồng xuất lịch thanh toán theo mẫu (Feature-0379). */
  @observable isLoadingExportGeneratedSchedule = false
  @observable tableData!: PagedResultDto<any>
  @observable tableARContract!: PagedResultDto<any>
  @observable listContractByCustomer!: any[]

  @observable tableDataForPinkBook!: PagedResultDto<any>
  @observable detailData!: any
  @observable auditLogData!: any
  @observable tableContractEvent!: PagedResultDto<any>
  @observable detailContractEvent!: any
  @observable paymentScheduleDetailData!: any
  @observable listBookingForContract!: any[]
  @observable listBookingForContractGroupProject!: any[]
  @observable printReviewData!: any
  @observable isLoadingPrint!: boolean

  @observable unitUsePromotion!: any
  @observable contractGenerateHistory!: any

  constructor() {
    makeAutoObservable(this)
    this.isLoading = false
    this.tableData = { items: [], totalCount: 0 }
    this.tableARContract = { items: [], totalCount: 0 }

    this.tableContractEvent = { items: [], totalCount: 0 }
    this.tableDataForPinkBook = { items: [], totalCount: 0 }
    this.detailData = {}
    this.paymentScheduleDetailData = { paymentSchedules: [] }
    this.auditLogData = []
    this.listBookingForContract = []
    this.listBookingForContractGroupProject = []
    this.contractGenerateHistory = { items: [], totalCount: 0 }
    this.unitUsePromotion = {}
    this.listContractByCustomer = []
  }

  @action
  async setUnitUsePromotion(unit: any) {
    this.unitUsePromotion = unit
  }

  @action
  async createOrUpdate(body: any) {
    this.isLoading = true
    if (!body.id) {
      const result = await contractService
        .create(body)
        .finally(() => (this.isLoading = false))
      this.detailData = result
      this.tableData.items.push(result)
    } else {
      const result = await contractService
        .update(body)
        .finally(() => (this.isLoading = false))
      this.tableData.items = this.tableData.items.map((x) => {
        if (x.id === body.id) x = result
        return x
      })
    }
  }

  @action
  resetCreateState() {
    this.detailData = {
      id: 0,
      contractCustomers: [{}],
    }
    this.unitUsePromotion = undefined
  }

  @action
  async initDetail() {
    this.detailData = {
      id: 0,
      contractCustomers: [{}],
    }
  }

  /** Seed promotionPrograms from terminated parent contract for create-from-terminated flow */
  @action
  initDetailFromTerminated(parentData: any) {
    const promotionPrograms = ContractEventPromotionProgramModel.assigns(
      resolveContractPromotionPrograms(parentData)
    )
    this.detailData = {
      id: 0,
      contractCustomers: [{}],
      cartId: parentData?.cartId,
      promotionPrograms,
    }
  }

  @action
  async createContract(body: any) {
    this.isLoading = true
    const result = await contractService
      .create(body)
      .finally(() => (this.isLoading = false))
    this.detailData = result
  }

  @action
  async updateContract(body: any) {
    this.isLoading = true
    const result = await contractService
      .update(body)
      .finally(() => (this.isLoading = false))
    this.detailData = result
  }

  @action
  async active(id: number) {
    await contractService.active(id)
  }
  @action
  async bulkActive(id: number[]) {
    await contractService.bulkActive(id)
  }
  @action
  async get(id: number) {
    this.isLoading = true
    const result = await contractService
      .get(id)
      .finally(() => (this.isLoading = false))
    this.detailData = result
  }
  @action
  async emptyTable() {
    this.tableData = { items: [], totalCount: 0 }
  }
  @action
  async getAll(params: any) {
    this.isLoading = true
    const result = await contractService
      .getAll(params)
      .finally(() => (this.isLoading = false))
    this.tableData = result
    return result
  }

  @action
  async getARGenerateContract(params: any) {
    this.isLoading = true
    const result = await contractService
      .getARGenerateContract(params)
      .finally(() => (this.isLoading = false))
    this.tableARContract = result
  }

  @action
  async getAllByCustomer(params: any) {
    this.isLoading = true
    const result = await contractService
      .getAllByCustomer(params)
      .finally(() => (this.isLoading = false))
    this.listContractByCustomer = result
  }

  @action
  async getAllForPinkBook(params: any) {
    this.isLoading = true
    const result = await contractService
      .getAllForPinkBook(params)
      .finally(() => (this.isLoading = false))
    this.tableDataForPinkBook = result
  }
  @action
  async getAuditLog(id: any) {
    const result = await contractService.getAuditLog(id)

    this.auditLogData = result
  }
  @action
  async calculateContractValue(body) {
    const result = await contractService.calculateContractValue(body)

    return result
  }
  // Generate contract
  @action
  async emptyPrintData() {
    this.printReviewData = ""
  }
  @action
  async generateContract(body: any) {
    this.isLoadingPrint = true
    const result = await contractService.generateContract(body).finally(() => {
      this.isLoadingPrint = false
    })

    this.printReviewData = result
    return result
  }
  @action
  async getListHistoryGenerateContract(params: any) {
    const result = await contractService.getListHistoryGenerateContract(params)

    this.contractGenerateHistory = result
  }
  @action
  async updateGenerateContract(body: any) {
    this.isLoading = true
    await contractService
      .updateGenerateContract(body)
      .finally(() => (this.isLoading = false))
  }
  // CONTRACT PAYMENT SCHEDULE
  // (Dead methods removed: getPaymentScheduleDetails and createOrUpdateContractPaymentSchedule
  //  called non-existent BE endpoints that have no handler)

  @action
  public importMigration = async (file: File) => {
    this.isLoading = true
    try {
      const result = await contractService
        .importMigration(file)
        .finally(() => (this.isLoading = false))

      return result
    } catch (error) {
      this.isLoading = false
      throw error
    }
  }

  @action
  async getListBookingForContract(params: any) {
    const result = await contractService.getListBookingForContract(params)
    this.listBookingForContract = result

    const grouped = Object.values(
      this.listBookingForContract
        .map((item) => ({
          ...item,
          id: item?.id,
          projectId: item?.cart?.projectId,
          projectName:
            item?.unit?.project?.projectName ??
            item?.cart?.project?.projectName,
        }))
        .reduce((acc, item) => {
          if (!acc[item.projectId]) {
            acc[item.projectId] = {
              projectId: item.projectId,
              projectName: item.projectName,
              units: [],
            }
          }
          acc[item.projectId].units.push(item)
          return acc
        }, {})
    )
    this.listBookingForContractGroupProject = grouped
  }
  @action
  async exportARContract(contractIds: number[]) {
    this.isLoading = true
    return await contractService
      .exportARContract(contractIds)
      .finally(() => (this.isLoading = false))
  }

  @action
  public validateBeforeImport = async (file: File) => {
    this.isLoading = true
    try {
      const result = await contractService
        .validateBeforeImport(file)
        .finally(() => (this.isLoading = false))

      return result
    } catch (error) {
      this.isLoading = false
      throw error
    }
  }

  @action
  public importARGenerateContract = async (file: File) => {
    this.isLoading = true
    try {
      const result = await contractService
        .importARGenerateContract(file)
        .finally(() => (this.isLoading = false))

      return result
    } catch (error) {
      this.isLoading = false
      throw error
    }
  }

  @action
  async exportContractExcel(params: any) {
    this.isLoading = true
    try {
      const result = await contractService.exportContractExcel(params)
        .finally(() => (this.isLoading = false))
      return result
    } catch (error) {
      this.isLoading = false
        throw error
    }
  }
  /**
   * Feature-0371 — tiền kiểm khi mở modal xuất file sự kiện chuyển đổi.
   *
   * Dùng cờ loading RIÊNG (`isLoadingExportEventConvert`), không dùng chung `isLoading` của bảng: nếu
   * dùng chung thì nút sẽ quay mỗi lần danh sách hợp đồng tải lại dù không ai bấm.
   */
  @action
  public prepareExportContractEventConvert = async (
    contractIds: number[],
    eventType: ContractEventTypeEnum
  ): Promise<IPrepareExportContractEventConvertResult> => {
    this.isLoadingExportEventConvert = true
    try {
      return await contractService.prepareExportContractEventConvert(contractIds, eventType)
    } finally {
      this.isLoadingExportEventConvert = false
    }
  }

  /**
   * Feature-0371 — PHẢI return giá trị của service: `false` là ca BE trả 204 (không còn hợp đồng hợp
   * lệ). `await` rồi bỏ thì modal không biết để hiện cảnh báo và người dùng nghĩ đã xuất xong.
   */
  @action
  public exportContractEventConvertFromContract = async (
    body: IExportContractEventConvertFromContractInput
  ): Promise<boolean> => {
    this.isLoadingExportEventConvert = true
    try {
      return await contractService.exportContractEventConvertFromContract(body)
    } finally {
      this.isLoadingExportEventConvert = false
    }
  }

  /** Feature-0383 — tiền kiểm luồng xuất "Thay đổi chính sách bán hàng" (endpoint riêng, đổ CTKM ra ô). */
  @action
  public prepareExportContractEventSalesPolicy = async (
    contractIds: number[]
  ): Promise<IPrepareExportContractEventSalesPolicyResult> => {
    this.isLoadingExportEventConvert = true
    try {
      return await contractService.prepareExportContractEventSalesPolicy(contractIds)
    } finally {
      this.isLoadingExportEventConvert = false
    }
  }

  /** Feature-0383 — xuất file mồi "Thay đổi chính sách bán hàng". `false` = BE trả 204 (không còn HĐ hợp lệ). */
  @action
  public exportContractEventSalesPolicyFromContract = async (
    body: IExportContractEventSalesPolicyFromContractInput
  ): Promise<boolean> => {
    this.isLoadingExportEventConvert = true
    try {
      return await contractService.exportContractEventSalesPolicyFromContract(body)
    } finally {
      this.isLoadingExportEventConvert = false
    }
  }

  /**
   * Feature-0379 — xuất lịch thanh toán tính từ mẫu.
   *
   * PHẢI return kết quả của service: `null` là đã tải tệp xong, object là BE đã queue job ở nền —
   * `await` rồi bỏ thì modal không biết để toast + mở hộp thư tệp, user tưởng lệnh xuất thất bại.
   * Dùng cờ loading riêng để không làm bảng danh sách quay spinner ké.
   */
  @action
  public exportGeneratedPaymentSchedule = async (
    input: IExportGeneratedPaymentScheduleInput
  ): Promise<IExportQueuedResult | null> => {
    this.isLoadingExportGeneratedSchedule = true
    try {
      return await contractService.exportGeneratedPaymentSchedule(input)
    } finally {
      this.isLoadingExportGeneratedSchedule = false
    }
  }

  /**
   * Feature-0382 — xuất tệp Word gom bảng lịch thanh toán.
   *
   * Không có nhánh `202` nên không trả gì: về tới đây là tệp đã tải xong. Dùng lại cờ loading của
   * đường Excel — hai đường xuất chung một modal, một nút, không bao giờ chạy đồng thời.
   */
  @action
  public exportGeneratedPaymentScheduleDocx = async (
    input: IExportGeneratedPaymentScheduleDocxInput
  ): Promise<void> => {
    this.isLoadingExportGeneratedSchedule = true
    try {
      await contractService.exportGeneratedPaymentScheduleDocx(input)
    } finally {
      this.isLoadingExportGeneratedSchedule = false
    }
  }
}

export default ContractStore
